import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Box, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Button, Grid,
    MenuItem, Chip, Alert, CircularProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, TablePagination
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import api from '../services/api';

function Auditoria() {
    const [auditoria, setAuditoria] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [usuarios, setUsuarios] = useState([]);
    const [filtros, setFiltros] = useState({
        tabela: '',
        operacao: '',
        usuario_id: '',
        registro_id: '',
        data_inicio: '',
        data_fim: ''
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [total, setTotal] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [registroSelecionado, setRegistroSelecionado] = useState(null);

    useEffect(() => {
        loadUsuarios();
        loadAuditoria();
        loadTotal();
    }, [filtros, page, rowsPerPage]);

    const loadUsuarios = async () => {
        try {
            const response = await api.get('/usuarios/lista');
            setUsuarios(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    };

    const loadAuditoria = async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                ...filtros,
                limit: rowsPerPage.toString(),
                offset: (page * rowsPerPage).toString()
            });
            
            // Remove parâmetros vazios
            Array.from(params.entries()).forEach(([key, value]) => {
                if (!value) params.delete(key);
            });

            const response = await api.get(`/auditoria?${params.toString()}`);
            setAuditoria(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar auditoria:', error);
            setError(error.response?.data?.error || 'Erro ao carregar auditoria');
        } finally {
            setLoading(false);
        }
    };

    const loadTotal = async () => {
        try {
            const params = new URLSearchParams(filtros);
            Array.from(params.entries()).forEach(([key, value]) => {
                if (!value) params.delete(key);
            });

            const response = await api.get(`/auditoria/count?${params.toString()}`);
            setTotal(response.data.total || 0);
        } catch (error) {
            console.error('Erro ao contar auditoria:', error);
        }
    };

    const handleFilterChange = (field, value) => {
        setFiltros({ ...filtros, [field]: value });
        setPage(0); // Reset para primeira página ao filtrar
    };

    const handleResetFilters = () => {
        setFiltros({
            tabela: '',
            operacao: '',
            usuario_id: '',
            registro_id: '',
            data_inicio: '',
            data_fim: ''
        });
        setPage(0);
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copiado para a área de transferência!');
    };

    const handleViewDetails = (registro) => {
        setRegistroSelecionado(registro);
        setDialogOpen(true);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Se a data já vem formatada do backend (YYYY-MM-DD HH:mm:ss), formata diretamente
            // O backend agora retorna em UTC, então precisamos tratar como UTC
            if (typeof date === 'string') {
                // Formato: "2026-01-07 10:39:12" (UTC)
                const match = date.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
                if (match) {
                    const [, ano, mes, dia, hora, minuto, segundo] = match;
                    // Cria uma data em UTC e converte para o timezone local (America/Sao_Paulo)
                    const dataUTC = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:${segundo}Z`);
                    // Formata para o timezone local
                    const diaLocal = String(dataUTC.getDate()).padStart(2, '0');
                    const mesLocal = String(dataUTC.getMonth() + 1).padStart(2, '0');
                    const anoLocal = dataUTC.getFullYear();
                    const horaLocal = String(dataUTC.getHours()).padStart(2, '0');
                    const minutoLocal = String(dataUTC.getMinutes()).padStart(2, '0');
                    const segundoLocal = String(dataUTC.getSeconds()).padStart(2, '0');
                    return `${diaLocal}/${mesLocal}/${anoLocal}, ${horaLocal}:${minutoLocal}:${segundoLocal}`;
                }
                // Se já está em formato brasileiro, retorna como está
                if (date.includes('/')) {
                    return date;
                }
            }
            
            // Fallback: tenta converter (assume UTC se não tiver timezone)
            const d = new Date(date);
            if (isNaN(d.getTime())) {
                return date;
            }
            
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const ano = d.getFullYear();
            const hora = String(d.getHours()).padStart(2, '0');
            const minuto = String(d.getMinutes()).padStart(2, '0');
            const segundo = String(d.getSeconds()).padStart(2, '0');
            
            return `${dia}/${mes}/${ano}, ${hora}:${minuto}:${segundo}`;
        } catch {
            return date;
        }
    };

    const handleExportPDF = async () => {
        try {
            const params = new URLSearchParams(filtros);
            Array.from(params.entries()).forEach(([key, value]) => {
                if (!value) params.delete(key);
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${api.defaults.baseURL}/auditoria/exportar/pdf?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Erro ao exportar PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `auditoria_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao exportar PDF:', error);
            alert('Erro ao exportar PDF: ' + (error.message || 'Erro desconhecido'));
        }
    };

    const handleExportExcel = async () => {
        try {
            const params = new URLSearchParams(filtros);
            Array.from(params.entries()).forEach(([key, value]) => {
                if (!value) params.delete(key);
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${api.defaults.baseURL}/auditoria/exportar/excel?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Erro ao exportar Excel');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `auditoria_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao exportar Excel:', error);
            alert('Erro ao exportar Excel: ' + (error.message || 'Erro desconhecido'));
        }
    };

    const getOperacaoColor = (operacao) => {
        switch (operacao) {
            case 'INSERT': return 'success';
            case 'UPDATE': return 'warning';
            case 'DELETE': return 'error';
            default: return 'default';
        }
    };

    return (
        <Container maxWidth="xl">
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <HistoryIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Auditoria do Sistema
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Registro de todas as alterações realizadas no sistema (últimos 15 dias)
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Filtros
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<PdfIcon />}
                            onClick={handleExportPDF}
                            disabled={loading}
                        >
                            Exportar PDF
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<ExcelIcon />}
                            onClick={handleExportExcel}
                            disabled={loading}
                        >
                            Exportar Excel
                        </Button>
                    </Box>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            select
                            label="Tabela"
                            value={filtros.tabela}
                            onChange={(e) => handleFilterChange('tabela', e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">Todas</MenuItem>
                            <MenuItem value="cliente">Cliente</MenuItem>
                            <MenuItem value="processo">Processo</MenuItem>
                            <MenuItem value="andamento">Andamento</MenuItem>
                            <MenuItem value="advogado">Advogado</MenuItem>
                            <MenuItem value="prazo">Prazo</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            select
                            label="Operação"
                            value={filtros.operacao}
                            onChange={(e) => handleFilterChange('operacao', e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">Todas</MenuItem>
                            <MenuItem value="INSERT">Inserção</MenuItem>
                            <MenuItem value="UPDATE">Atualização</MenuItem>
                            <MenuItem value="DELETE">Exclusão</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            select
                            label="Usuário"
                            value={filtros.usuario_id}
                            onChange={(e) => handleFilterChange('usuario_id', e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {usuarios.map((usuario) => (
                                <MenuItem key={usuario.id} value={usuario.id}>
                                    {usuario.nome} ({usuario.username})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="ID do Registro"
                            value={filtros.registro_id}
                            onChange={(e) => handleFilterChange('registro_id', e.target.value)}
                            size="small"
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Data Início"
                            type="date"
                            value={filtros.data_inicio}
                            onChange={(e) => handleFilterChange('data_inicio', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Data Fim"
                            type="date"
                            value={filtros.data_fim}
                            onChange={(e) => handleFilterChange('data_fim', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={loadAuditoria}
                                fullWidth
                            >
                                Buscar
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleResetFilters}
                            >
                                Limpar
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data/Hora</TableCell>
                            <TableCell>Usuário</TableCell>
                            <TableCell>Tabela</TableCell>
                            <TableCell>Operação</TableCell>
                            <TableCell>ID Registro</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : auditoria.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Nenhum registro encontrado
                                </TableCell>
                            </TableRow>
                        ) : (
                            auditoria.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>{formatDate(item.timestamp)}</TableCell>
                                    <TableCell>{item.usuario_nome || item.usuario_username || `ID: ${item.usuario_id}`}</TableCell>
                                    <TableCell>{item.tabela}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.operacao}
                                            color={getOperacaoColor(item.operacao)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{item.registro_id}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleViewDetails(item)}
                                            color="primary"
                                        >
                                            Ver Detalhes
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Registros por página:"
                />
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Detalhes da Auditoria
                </DialogTitle>
                <DialogContent>
                    {registroSelecionado && (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Data/Hora</Typography>
                                    <Typography variant="body1">{formatDate(registroSelecionado.timestamp)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Usuário</Typography>
                                    <Typography variant="body1">
                                        {registroSelecionado.usuario_nome || registroSelecionado.usuario_username || `ID: ${registroSelecionado.usuario_id}`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Tabela</Typography>
                                    <Typography variant="body1">{registroSelecionado.tabela}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Operação</Typography>
                                    <Chip
                                        label={registroSelecionado.operacao}
                                        color={getOperacaoColor(registroSelecionado.operacao)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">ID do Registro</Typography>
                                    <Typography variant="body1">{registroSelecionado.registro_id}</Typography>
                                </Grid>
                                {registroSelecionado.ip_address && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">IP</Typography>
                                        <Typography variant="body1">{registroSelecionado.ip_address}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">Dados Anteriores</Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(JSON.stringify(registroSelecionado.dados_anteriores, null, 2))}
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                                            {JSON.stringify(registroSelecionado.dados_anteriores, null, 2)}
                                        </pre>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">Dados Atuais</Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(JSON.stringify(registroSelecionado.dados_atuais, null, 2))}
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                                            {JSON.stringify(registroSelecionado.dados_atuais, null, 2)}
                                        </pre>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default Auditoria;

